import isMobileDevice from '@core/utils/isMobileDevice';
import { SettingGroupID } from 'types';

export enum SidebarPosition {
	HIDDEN = 'Hidden',
	LEFT = 'Left',
	RIGHT = 'Right',
}

/**
 * mode MPR
 * Các panel có thể hiển trong side tool panel
 */
export enum SubPanelMpr {
	MPR_LAYOUT = 'Mpr layout',
	MPR_TOOL = 'Mpr tools',
	WINDOW_LEVEL = 'Window level',
	SERIES_THUMBNAILS = 'Series thumbnails',
}
/**
 * mode 2D
 * Các panel có thể hiển trong side tool panel
 */
export enum SubPanel2D {
	SERIES_LAYOUT = 'Series layout',
	IMAGE_TOOL = 'Image tools',
	STUDY_TIMELINE = 'Study Timeline',
	WINDOW_LEVEL = 'Window/Level Presets',
	SERIES_THUMBNAILS = 'Series thumbnails',
}

/**
 * mode MPR
 * Các panel có thể hiển trong side tool panel
 */
export enum SubPanel3D {
	LAYOUT = 'Layout',
	RENDERING_TOOLS = 'Rendering tools',
	ADVANCED_TOOLS = 'Advanced tools',
	SERIES_THUMBNAILS = 'Series thumbnails',
}
/**
 * mode MPR
 * Các panel có thể hiển trong side tool panel
 */
export enum SubPanelDicomPrint {
	DicomPrintConfig = 'Dicom print config',
}
export type SidebarSubPanel = SubPanelMpr | SubPanel2D | SubPanel3D | SubPanelDicomPrint;

/**
 * 2 Panel chính trong app
 */
export type SidePanelType = 'toolSidebar' | 'seriesSidebar';

export type SubPanelConfig = {
	[key in SidebarSubPanel]?: boolean;
};

export type SidePanelConfig = {
	position?: `${SidebarPosition}`;
	subPanel?: SubPanelConfig;
};

export enum Viewport3DRenderMode {
	Client = 'Client',
	Server = 'Server',
}

export type Viewport3DConfig = {
	renderMode?: Viewport3DRenderMode;
	samplingDistance?: number;
};

export enum ViewportMPRRenderMode {
	Client = 'Client',
	Server = 'Server',
}

export type ViewportMPRConfig = {
	renderMode?: ViewportMPRRenderMode;
};

type BaseViewSetting = Partial<Record<SidePanelType, SidePanelConfig>>;

export type ViewSetting = BaseViewSetting & {
	renderMode?: Viewport3DRenderMode | ViewportMPRRenderMode;
	samplingDistance?: number;
};

export const DefaultViewConfig: Record<SettingGroupID, ViewSetting> = {
	[SettingGroupID.default]: {
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: true,
				[SubPanelMpr.MPR_LAYOUT]: false,
				[SubPanel2D.SERIES_LAYOUT]: true,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel3D.ADVANCED_TOOLS]: false,
				[SubPanel3D.RENDERING_TOOLS]: false,
				[SubPanel3D.LAYOUT]: false,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: true,
			},
		},
	},
	[SettingGroupID.mpr]: {
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: true,
				[SubPanelMpr.MPR_TOOL]: true,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel2D.WINDOW_LEVEL]: true,
				[SubPanel3D.ADVANCED_TOOLS]: false,
				[SubPanel3D.RENDERING_TOOLS]: false,
				[SubPanel3D.LAYOUT]: false,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.remoteMPR]: {
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: true,
				[SubPanelMpr.MPR_TOOL]: true,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: true,
				[SubPanel2D.WINDOW_LEVEL]: true,
				[SubPanel3D.ADVANCED_TOOLS]: false,
				[SubPanel3D.RENDERING_TOOLS]: false,
				[SubPanel3D.LAYOUT]: false,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.remoteRender3D]: {
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: false,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel3D.ADVANCED_TOOLS]: true,
				[SubPanel3D.RENDERING_TOOLS]: true,
				[SubPanel3D.LAYOUT]: true,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
			},
		},
	},
	[SettingGroupID.clientRender3D]: {
		// Đặt cấu hình default của client giống với remote.
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: false,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel3D.ADVANCED_TOOLS]: true,
				[SubPanel3D.RENDERING_TOOLS]: true,
				[SubPanel3D.LAYOUT]: true,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.fusionPetCT]: {
		seriesSidebar: { position: 'Hidden' },
		toolSidebar: {
			position: 'Hidden',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: true,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel2D.WINDOW_LEVEL]: true,
				[SubPanel3D.ADVANCED_TOOLS]: false,
				[SubPanel3D.RENDERING_TOOLS]: false,
				[SubPanel3D.LAYOUT]: false,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.remoteRenderEndo]: {
		seriesSidebar: { position: 'Hidden' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: true,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: true,
				[SubPanel3D.ADVANCED_TOOLS]: true,
				[SubPanel3D.RENDERING_TOOLS]: true,
				[SubPanel3D.LAYOUT]: true,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.mammo]: {
		seriesSidebar: { position: 'Hidden' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: true,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: true,
				[SubPanel3D.ADVANCED_TOOLS]: true,
				[SubPanel3D.RENDERING_TOOLS]: true,
				[SubPanel3D.LAYOUT]: true,
				[SubPanelDicomPrint.DicomPrintConfig]: false,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
	[SettingGroupID.render3D]: {
		renderMode: isMobileDevice() ? Viewport3DRenderMode.Server : Viewport3DRenderMode.Client, // client side hoặc server side
	},
	[SettingGroupID.renderMPR]: {
		renderMode: isMobileDevice() ? ViewportMPRRenderMode.Server : ViewportMPRRenderMode.Client,
	},
	[SettingGroupID.dicomPrint]: {
		seriesSidebar: { position: 'Right' },
		toolSidebar: {
			position: 'Left',
			subPanel: {
				[SubPanel2D.IMAGE_TOOL]: false,
				[SubPanelMpr.MPR_LAYOUT]: false,
				[SubPanel2D.SERIES_LAYOUT]: false,
				[SubPanelMpr.MPR_TOOL]: false,
				[SubPanel2D.WINDOW_LEVEL]: false,
				[SubPanel2D.SERIES_THUMBNAILS]: false,
				[SubPanel3D.ADVANCED_TOOLS]: false,
				[SubPanel3D.RENDERING_TOOLS]: false,
				[SubPanel3D.LAYOUT]: false,
				[SubPanelDicomPrint.DicomPrintConfig]: true,
				[SubPanel2D.STUDY_TIMELINE]: false,
			},
		},
	},
};
